import { and } from 'tio-ui/utilities';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import Drawer, { DrawerBody } from 'tio-reimagined/components/drawer';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import GuidelinesModal from './guidelines-modal';
import ActivityView from './activity-view';
import CardTransactionRow from './card-transaction-row';
import CurrencySuperscriptCents from 'tio-common/components/currency-superscript-cents';
import type { CourseCashTransaction } from 'tio-common/types/course-cash-transaction';
import type {
  Stripe,
  StripeElements,
  StripeConstructorOptions,
  StripeIssuingCardNumberDisplayElement,
  StripeIssuingCardCvcDisplayElement,
  StripeIssuingCardExpiryDisplayElement,
  StripeIssuingCardPinDisplayElement,
  // StripeIssuingCardCopyButtonElement,
} from '@stripe/stripe-js';
import { modifier } from 'ember-modifier';
import { hash } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import DescriptionList from 'tio-ui/components/description-list';
import ENV from 'tio-employee/config/environment';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { PhActivity, PhClock, PhEye, PhEyeSlash } from 'ember-phosphor-icons';
import type Owner from '@ember/owner';
import type Store from 'tio-employee/services/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type Intl from 'ember-intl/services/intl';

type IssuingName =
  | 'issuingCardNumberDisplay'
  | 'issuingCardCvcDisplay'
  | 'issuingCardExpiryDisplay'
  | 'issuingCardPinDisplay';
// | 'issuingCardCopyButton';

type AnyIssuingEl =
  | StripeIssuingCardNumberDisplayElement
  | StripeIssuingCardCvcDisplayElement
  | StripeIssuingCardExpiryDisplayElement
  | StripeIssuingCardPinDisplayElement;
// | StripeIssuingCardCopyButtonElement;

export interface StripeCardDisplaySignature {
  Args: {
    application: TasApplicationModel;
    transactions: CourseCashTransaction[];
    onUploadReceipt?: (transaction: CourseCashTransaction) => void;
    showReceiptStatus?: boolean;
  };
  Element: HTMLElement;
}

let stripePromise: Promise<Stripe | null> | undefined;
const getStripe = (
  pk: string,
  opts: StripeConstructorOptions = {},
  loadStripeFn: (pk: string, opts?: StripeConstructorOptions) => Promise<Stripe | null>
) => {
  if (!stripePromise) {
    stripePromise = loadStripeFn(pk, opts);
  }
  return stripePromise;
};

const stripePublishableKey = {
  staging:
    'pk_test_51QVKAvGwNyD6Nr0b1MSw8J18o9gaYMSsBdhk4pM3kH4OYvuROqqMAgvBymnuBj10LLvVMW0Ndt6FHIK8jvH2zKoH00UqCMY5BA',
  production:
    'pk_live_51QVKAvGwNyD6Nr0bPB77ZhKsqDorlZfywALFLfHkxMmlN65KzZ8fW7G8WekmVNcmzmZBr75hc60pBafqpdvWHGuk00c8OKlcAJ',
};

type IssuingAuth = { nonce: string; ephemeralKeySecret: string };

const cardNumberStyles = {
  base: {
    iconColor: '#c4f0ff',
    color: '#fff',
    fontWeight: '400',
    fontFamily: 'Montserrat, Open Sans, Segoe UI, sans-serif',
    fontSize: '24px',
  },
};

const cardDetailsStyles = {
  base: {
    iconColor: '#c4f0ff',
    color: '#fff',
    fontWeight: '400',
    fontFamily: 'Montserrat, Open Sans, Segoe UI, sans-serif',
    fontSize: '16px',
  },
};

const descriptionListStripeStyles = {
  base: {
    color: '#374151',
    fontWeight: '500',
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '14px',
    lineHeight: '1.5rem',
  },
};

const mountIssuing = modifier(
  (
    element: HTMLElement,
    [name, styleType, elements, issuingCardId, auth, register]: [
      IssuingName,
      'cardNumber' | 'cardDetails' | 'descriptionList',
      StripeElements,
      string,
      IssuingAuth,
      (el: AnyIssuingEl) => void,
    ]
  ) => {
    let stripeStyles;
    if (styleType === 'cardNumber') {
      stripeStyles = cardNumberStyles;
    } else if (styleType === 'descriptionList') {
      stripeStyles = descriptionListStripeStyles;
    } else {
      stripeStyles = cardDetailsStyles;
    }
    let issuingEl: AnyIssuingEl;

    switch (name) {
      case 'issuingCardNumberDisplay':
        issuingEl = elements.create('issuingCardNumberDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
      case 'issuingCardCvcDisplay':
        issuingEl = elements.create('issuingCardCvcDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
      case 'issuingCardExpiryDisplay':
        issuingEl = elements.create('issuingCardExpiryDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
      case 'issuingCardPinDisplay':
        issuingEl = elements.create('issuingCardPinDisplay', {
          issuingCard: issuingCardId,
          ...auth,
          style: stripeStyles,
        });
        break;
      // case 'issuingCardCopyButton':
      //   issuingEl = elements.create('issuingCardCopyButton', {
      //     issuingCard: issuingCardId,
      //     ...auth,
      //     style: stripeStyles,
      //   });
    }
    issuingEl.mount(element);
    register?.(issuingEl);
    return () => issuingEl.destroy();
  }
);

export default class StripeCardDisplay extends Component<StripeCardDisplaySignature> {
  @service declare store: Store;
  @service declare intl: Intl;
  private issuingEls = new Set<AnyIssuingEl>();
  private refreshTimer?: number;
  @tracked private elements!: StripeElements;
  @tracked private auth!: IssuingAuth;
  @tracked showCardDetails = false;
  @tracked showSensitiveDetails = true;
  @tracked showGuidelinesModal = false;
  @tracked isActivityDrawerOpen = false;

  constructor(owner: Owner, args: StripeCardDisplaySignature['Args']) {
    super(owner, args);
    const companyAccountProviderId = this.card?.pcsCompany?.providerId || '';
    if (companyAccountProviderId) {
      this.setup(companyAccountProviderId);
    }
  }

  get showReceiptStatus() {
    return this.args.showReceiptStatus !== false;
  }

  get card() {
    return (
      this.args.application.pcsCards?.find((card) => card.isActive) ??
      this.args.application.pcsCards?.[0]
    );
  }

  get isCardActive() {
    return this.card?.isActive ?? false;
  }

  get isCardDeactivated() {
    return this.card?.isDeactivated ?? false;
  }

  get isCardActiveOrDeactivated() {
    return this.isCardActive || this.isCardDeactivated;
  }

  get stripeCardProviderId() {
    return this.card?.providerId || '';
  }

  get cardId() {
    return this.card?.id || '';
  }

  get apiHost() {
    // @ts-expect-error - Ember Data adapter property not typed on MinimumAdapterInterface
    return this.store.adapterFor('application').host;
  }

  get headers() {
    // @ts-expect-error - Ember Data adapter property not typed on MinimumAdapterInterface
    return this.store.adapterFor('application').headers;
  }

  exchangeNonce = async (cardId: string, nonce: string) => {
    const response = await fetch(
      `${this.apiHost}/pcs-cards/${encodeURIComponent(cardId)}/client-token`,
      {
        method: 'POST',
        headers: {
          ...this.headers,
          'content-type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ nonce }),
      }
    );
    const json = await response.json().catch(() => ({}));
    if (!response.ok)
      throw new Error(
        `client-token ${response.status} ${response.statusText} — ${JSON.stringify(json)}`
      );
    const secret = json?.secret;
    if (typeof secret !== 'string' || !secret)
      throw new Error('Missing ephemeral key secret in response');

    return secret;
  };

  async setup(companyAccountProviderId: string) {
    const publishableKey =
      ENV.tioEnvironment === 'production'
        ? stripePublishableKey.production
        : stripePublishableKey.staging;

    // Dynamically import Stripe.js only when needed
    const { loadStripe } = await import('@stripe/stripe-js');
    const stripe = await getStripe(
      publishableKey,
      {
        stripeAccount: companyAccountProviderId,
      },
      loadStripe
    );
    if (!stripe) throw new Error('Stripe.js failed to load');
    this.elements = stripe.elements();

    // get creds once
    await this.refreshAuth(stripe);

    // refresh just before expiration (every 15 mins)
    this.refreshTimer = window.setInterval(() => this.refreshAuth(stripe), 14 * 60 * 1000);
  }

  private async refreshAuth(stripe: Stripe) {
    const { nonce } = await stripe.createEphemeralKeyNonce({
      issuingCard: this.stripeCardProviderId,
    });
    if (!nonce) throw new Error('Nonce missing');
    if (!this.cardId) throw new Error('Card ID missing');
    const ephemeralKeySecret = await this.exchangeNonce(this.cardId, nonce);
    this.auth = { nonce, ephemeralKeySecret };
    // push fresh creds to any mounted elements
    this.issuingEls.forEach((el) => el.update(this.auth));
  }

  register = (el: AnyIssuingEl) => {
    this.issuingEls.add(el);
  };

  willDestroy() {
    if (this.refreshTimer) window.clearInterval(this.refreshTimer);
    this.issuingEls.forEach((el) => el.destroy());
    this.issuingEls.clear();
  }

  get lastFour() {
    return (this.card?.providerData?.last4 as string) ?? '';
  }

  get legalName() {
    return (this.card?.pcsCardHolder?.legalName as string) ?? '';
  }

  get companyName() {
    return (this.card?.pcsCompany?.providerData?.company?.name as string) ?? '';
  }

  get companyAddressLine1() {
    return (this.card?.pcsCompany?.providerData?.company?.address?.line1 as string) ?? '';
  }

  get companyCity() {
    return (this.card?.pcsCompany?.providerData?.company?.address?.city as string) ?? '';
  }

  get companyState() {
    return (this.card?.pcsCompany?.providerData?.company?.address?.state as string) ?? '';
  }

  get companyZipCode() {
    return (this.card?.pcsCompany?.providerData?.company?.address?.postal_code as string) ?? '';
  }

  get formattedBillingAddress() {
    const parts = [
      this.companyAddressLine1,
      [this.companyCity, this.companyState].filter(Boolean).join(', '),
      this.companyZipCode,
    ].filter(Boolean);

    return parts.join(', ');
  }

  get zipCode() {
    return (this.card?.pcsCardHolder?.providerData?.billing?.address?.postal_code as string) ?? '';
  }

  get formattedCardFinalApprovedTotal() {
    const cents = (this.args.application.amounts.course_cash.reserved ??
      this.args.application.cardFinalApprovedTotal ??
      this.args.application.cardPreApprovedTotal ??
      0) as number;
    const dollars = cents / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
    });
  }

  get cardReservedAmountCents() {
    // Backend returns the remaining available amount (already accounts for used/refunded)
    return (this.args.application.amounts.course_cash.reserved ?? 0) as number;
  }

  get cardUsedAmountCents() {
    return (this.args.application.amounts.course_cash.used ?? 0) as number;
  }

  get cardRemainingAmountCents() {
    return Math.max(0, this.cardReservedAmountCents);
  }

  get formattedCardUsedAmount() {
    const dollars = this.cardUsedAmountCents / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
    });
  }

  get formattedCardRemainingAmount() {
    const dollars = this.cardRemainingAmountCents / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
    });
  }

  get cardBalanceMessage() {
    const schoolName = this.args.application.tasProgramInstance.institutionName;

    if (this.cardRemainingAmountCents <= 0) {
      return this.intl.t('tas.stripe_details.card_fully_used');
    } else if (this.cardUsedAmountCents > 0) {
      return this.intl.t('tas.stripe_details.card_partially_used', {
        usedAmount: this.formattedCardUsedAmount,
        remainingAmount: this.formattedCardRemainingAmount,
      });
    } else {
      return this.intl.t('tas.stripe_details.your_card_is_ready', {
        schoolName,
        amount: this.formattedCardFinalApprovedTotal,
      });
    }
  }

  toggleCardDetails = () => {
    this.showCardDetails = !this.showCardDetails;
  };

  toggleSensitiveDetails = () => {
    this.showSensitiveDetails = !this.showSensitiveDetails;
  };

  toggleGuidelinesModal = () => {
    this.showGuidelinesModal = !this.showGuidelinesModal;
  };

  openActivityDrawer = () => {
    this.isActivityDrawerOpen = true;
  };

  closeActivityDrawer = () => {
    this.isActivityDrawerOpen = false;
  };

  get pcsCards() {
    return this.args.application.pcsCards ?? [];
  }

  get pcsCardsWithMeta() {
    const cards = this.pcsCards;
    return cards.map((card, i) => ({ card, cardNumber: i + 1, totalCards: cards.length }));
  }

  // Transaction-related getters for Phase 8 implementation
  get transactions() {
    return this.args.transactions;
  }

  get unverifiedTransactions() {
    return this.transactions.filter((t) => t.receiptStatus === 'needed');
  }

  get hasUnverifiedTransactions() {
    return this.unverifiedTransactions.length > 0;
  }

  get allTransactionsForDisplay() {
    // Return all transactions except not_required for display in card section
    return this.transactions.filter((t) => t.receiptStatus !== 'not_required');
  }

  get verificationSummary() {
    const txns = this.transactions;
    const needed = txns.filter((t) => t.receiptStatus === 'needed').length;
    const inReview = txns.filter((t) => t.receiptStatus === 'uploaded').length;
    const verified = txns.filter((t) => t.receiptStatus === 'verified').length;
    const rejected = txns.filter((t) => t.receiptStatus === 'rejected').length;
    const changesNeeded = txns.filter((t) => t.receiptStatus === 'changes_needed').length;
    return { needed, inReview, verified, rejected, changesNeeded };
  }

  get hasVerificationActivity() {
    const s = this.verificationSummary;
    return (
      s.needed > 0 || s.inReview > 0 || s.verified > 0 || s.rejected > 0 || s.changesNeeded > 0
    );
  }

  get amountUsed() {
    // Sum of all transaction amounts (already in cents and positive)
    return this.transactions.reduce((sum, t) => sum + t.amount, 0);
  }

  get cardLimit() {
    // Get the card spending limit in cents
    const cents = (this.args.application.amounts.course_cash.reserved ??
      this.args.application.cardFinalApprovedTotal ??
      this.args.application.cardPreApprovedTotal ??
      0) as number;
    return cents;
  }

  <template>
    <Section>
      <:header>
        <CourseCashTag
          @isDirectPayProgram={{true}}
          @size="sm"
          @buttonClass="text-tio-gray-700"
          @disablePopover={{true}}
          class="text-inherit"
          aria-hidden="true"
        >
          <:beforeTag>
            <span>{{t "tas.stripe_details.your"}}</span>
          </:beforeTag>
          <:afterTag>
            <span>{{t "tas.stripe_details.card"}}</span>
          </:afterTag>
        </CourseCashTag>
      </:header>
      <:actions>
        <Button
          @appearance="minimal"
          @intent="primary"
          @size="sm"
          {{on "click" this.openActivityDrawer}}
          data-test-view-card-activity
        >
          <PhActivity @size={{16}} class="mr-1" />
          {{t "tuition_assistance.course_cash.activity.view_activity"}}
        </Button>
      </:actions>
      <:body>
        {{#if this.isCardActive}}
          <div class="flex flex-col lg:flex-row gap-6 items-start">
            <div class="flex-1 max-w-md">
              {{! Card Balance - Always visible }}
              <div class="mb-4">
                <p class="text-xs text-gray-600 uppercase tracking-wide mb-1">{{t
                    "tas.stripe_details.card_balance"
                  }}</p>
                <p class="text-2xl font-semibold mb-4">
                  <CurrencySuperscriptCents @amountCents={{this.cardRemainingAmountCents}} />
                </p>
                <p class="text-sm text-balance mb-2">{{this.cardBalanceMessage}}</p>
                <p class="text-xs italic mb-4">
                  {{t "tas.stripe_details.any_merchant_transaction_fees"}}
                </p>
              </div>

              {{#unless this.showCardDetails}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.toggleCardDetails}}
                  title={{t "common.save"}}
                  class="grow-0"
                >
                  {{t "tas.stripe_details.view_card_details"}}
                </Button>
              {{/unless}}

              {{#if this.showCardDetails}}
                <div class="relative">
                  {{! Instructional text }}
                  <p class="text-sm text-gray-700 mb-4">
                    {{htmlSafe (t "tas.stripe_details.to_use_your_card_instructions")}}
                  </p>

                  {{! Payment Information section with eye icon }}
                  <div
                    class="bg-[rgba(238,237,239,0.5)] border border-[rgba(238,237,239,0.8)] rounded-[10px] px-4 py-2.5 flex items-center justify-between mb-3"
                  >
                    <h4 class="text-base font-semibold">
                      {{t "tas.stripe_details.payment_information"}}
                    </h4>
                    <button
                      type="button"
                      class="cursor-pointer"
                      {{on "click" this.toggleSensitiveDetails}}
                      aria-label={{if
                        this.showSensitiveDetails
                        "Hide card details"
                        "Show card details"
                      }}
                    >
                      {{#if this.showSensitiveDetails}}
                        <PhEye @size={{18}} />
                      {{else}}
                        <PhEyeSlash @size={{18}} />
                      {{/if}}
                    </button>
                  </div>

                  {{! Card Details DescriptionList }}
                  <DescriptionList @cols={{2}} @divided={{true}} @compact={{true}} as |List|>
                    {{! Cardholder Name }}
                    <List.Group data-test-group>
                      <List.Term>{{t "tas.stripe_details.cardholder_name"}}</List.Term>
                      <List.Details class="font-semibold tracking-wide">
                        {{#if this.showSensitiveDetails}}
                          {{this.legalName}}
                        {{else}}
                          **** ****
                        {{/if}}
                      </List.Details>
                    </List.Group>

                    {{! Company Name }}
                    {{#if this.companyName}}
                      <List.Group>
                        <List.Term>{{t "tas.stripe_details.company_name"}}</List.Term>
                        <List.Details
                          class="font-semibold tracking-wide"
                        >{{this.companyName}}</List.Details>
                      </List.Group>
                    {{/if}}

                    {{! Card Number - Stripe Element or Masked }}
                    <List.Group>
                      <List.Term>{{t "tas.stripe_details.card_number"}}</List.Term>
                      <List.Details class="font-semibold tracking-wide">
                        {{#if this.showSensitiveDetails}}
                          {{#if (and this.auth this.elements)}}
                            <div
                              class="min-h-6"
                              {{mountIssuing
                                "issuingCardNumberDisplay"
                                "descriptionList"
                                this.elements
                                this.stripeCardProviderId
                                this.auth
                                this.register
                              }}
                            />
                          {{/if}}
                        {{else}}
                          **** **** ****
                          {{this.lastFour}}
                        {{/if}}
                      </List.Details>
                    </List.Group>

                    {{! CVV - Stripe Element or Masked }}
                    <List.Group>
                      <List.Term>{{t "tas.stripe_details.cvv"}}</List.Term>
                      <List.Details class="font-semibold tracking-wide">
                        {{#if this.showSensitiveDetails}}
                          {{#if (and this.auth this.elements)}}
                            <div
                              class="min-h-6"
                              {{mountIssuing
                                "issuingCardCvcDisplay"
                                "descriptionList"
                                this.elements
                                this.stripeCardProviderId
                                this.auth
                                this.register
                              }}
                            />
                          {{/if}}
                        {{else}}
                          ***
                        {{/if}}
                      </List.Details>
                    </List.Group>

                    {{! Expiration - Stripe Element or Masked }}
                    <List.Group>
                      <List.Term>{{t "tas.stripe_details.expiration_date"}}</List.Term>
                      <List.Details class="font-semibold tracking-wide">
                        {{#if this.showSensitiveDetails}}
                          {{#if (and this.auth this.elements)}}
                            <div
                              class="min-h-6"
                              {{mountIssuing
                                "issuingCardExpiryDisplay"
                                "descriptionList"
                                this.elements
                                this.stripeCardProviderId
                                this.auth
                                this.register
                              }}
                            />
                          {{/if}}
                        {{else}}
                          **/**/****
                        {{/if}}
                      </List.Details>
                    </List.Group>

                    {{! Billing Address - Masked }}
                    {{#if this.formattedBillingAddress}}
                      <List.Group>
                        <List.Term>{{t "tas.stripe_details.billing_address"}}</List.Term>
                        <List.Details class="font-semibold tracking-wide">
                          {{#if this.showSensitiveDetails}}
                            {{this.formattedBillingAddress}}
                          {{else}}
                            **** ******* *** **********, ** *****
                          {{/if}}
                        </List.Details>
                      </List.Group>
                    {{/if}}
                  </DescriptionList>
                </div>
              {{/if}}
            </div>
            <div aria-labelledby="card-number-label">
              <div class="my-2">
                <div
                  class="relative mx-auto w-80 lg:w-96 aspect-[85.6/54] rounded-2xl bg-linear-to-tr from-violet-900 to-violet-300 text-white shadow-xl ring-1 ring-white/10"
                >
                  {{! background / brand, kept behind }}
                  <div
                    class="absolute inset-0 z-0 pointer-events-none flex items-end justify-end px-5 pb-4"
                  >
                    {{svgJar "visa" height="40%" role="img" desc="VISA"}}
                  </div>

                  <div class="absolute inset-0 px-5 py-4 flex flex-col gap-3 justify-end">

                    {{#if (and this.auth this.showCardDetails this.showSensitiveDetails)}}
                      {{! Card number }}
                      <div
                        class="block min-h-8"
                        {{mountIssuing
                          "issuingCardNumberDisplay"
                          "cardNumber"
                          this.elements
                          this.stripeCardProviderId
                          this.auth
                          this.register
                        }}
                      />

                      {{! CVV}}
                      <div class="flex gap-8">
                        <div class="flex flex-col gap-1">
                          <span
                            class="text-[10px] font-medium uppercase tracking-widest text-white"
                          >{{t "tas.stripe_details.cvv"}}</span>
                          <div class="w-8">
                            <div
                              class="block min-h-6"
                              {{mountIssuing
                                "issuingCardCvcDisplay"
                                "cardDetails"
                                this.elements
                                this.stripeCardProviderId
                                this.auth
                                this.register
                              }}
                            />
                          </div>
                        </div>

                        <div class="flex flex-col gap-1">
                          <span
                            class="text-[10px] font-medium uppercase tracking-widest text-white"
                          >{{t "tas.stripe_details.valid_thru"}}</span>
                          <div
                            class="block min-h-6"
                            {{mountIssuing
                              "issuingCardExpiryDisplay"
                              "cardDetails"
                              this.elements
                              this.stripeCardProviderId
                              this.auth
                              this.register
                            }}
                          />
                        </div>
                      </div>
                    {{else}}
                      <p class="tracking-widest sm:text-lg font-semibold py-2">
                        <span aria-hidden="true">• • • •</span>
                        <span class="ml-1">{{this.lastFour}}</span>
                        <span class="sr-only">{{t "tas.stripe_details.card_ending_in"}}
                          {{this.lastFour}}</span>
                      </p>
                    {{/if}}
                  </div>
                </div>
              </div>
            </div>
          </div>
        {{else if this.isCardDeactivated}}
          <div class="flex flex-col lg:flex-row gap-6 items-start">
            <div class="flex-1 max-w-md" data-test-deactivated-card-alert>
              <AlertBanner
                @intent="warning"
                @title={{t "tas.stripe_details.card_deactivated"}}
                class="mb-4"
              >
                {{t "tas.stripe_details.card_deactivated_message"}}
              </AlertBanner>
            </div>
            <div aria-label={{t "tas.stripe_details.card_deactivated"}}>
              <div class="my-2">
                <div
                  class="relative mx-auto w-80 lg:w-96 aspect-[85.6/54] rounded-2xl bg-linear-to-tr from-gray-500 to-gray-300 text-white shadow-xl ring-1 ring-white/10 opacity-60"
                >
                  <div
                    class="absolute inset-0 z-0 pointer-events-none flex items-end justify-end px-5 pb-4"
                  >
                    {{svgJar "visa" height="40%" role="img" desc="VISA"}}
                  </div>
                  <div class="absolute inset-0 px-5 py-4 flex flex-col gap-3 justify-end">
                    <p class="tracking-widest sm:text-lg font-semibold py-2">
                      <span aria-hidden="true">• • • • • • • • • • • • • • • •</span>
                      <span class="sr-only">{{t "tas.stripe_details.card_deactivated"}}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        {{else}}
          <div class="flex flex-col items-center justify-center py-4 px-4 text-center">
            <div class="p-6 w-full">
              <div class="mb-3">
                <PhClock @size={{48}} class="mx-auto text-violet-600" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">
                {{t "tas.stripe_details.card_activation_pending"}}
              </h3>
              <p class="text-sm text-gray-600">
                {{t "tas.stripe_details.card_activation_message"}}
              </p>
            </div>
          </div>
        {{/if}}

        {{#if this.isCardActiveOrDeactivated}}
          {{! Transaction List Section }}
          {{#if this.allTransactionsForDisplay.length}}
            <div class="mt-6 pt-6 border-t border-gray-200" data-test-transactions-section>
              <div class="flex items-center justify-between mb-3">
                <div class="text-xs font-semibold text-gray-500 tracking-wider uppercase">
                  {{t "tas.course_cash.transactions"}}
                </div>
                {{#if (and this.hasVerificationActivity this.showReceiptStatus)}}
                  <div class="flex gap-2 flex-wrap" data-test-verification-summary>
                    {{#if this.verificationSummary.needed}}
                      <Chip @intent="danger" @appearance="faded" @size="sm">
                        {{this.verificationSummary.needed}}
                        {{t "tas.course_cash.needs_receipt"}}
                      </Chip>
                    {{/if}}
                    {{#if this.verificationSummary.inReview}}
                      <Chip @intent="warning" @appearance="faded" @size="sm">
                        {{this.verificationSummary.inReview}}
                        {{t "tas.course_cash.in_review"}}
                      </Chip>
                    {{/if}}
                    {{#if this.verificationSummary.verified}}
                      <Chip @intent="success" @appearance="faded" @size="sm">
                        {{this.verificationSummary.verified}}
                        {{t "tas.course_cash.verified"}}
                      </Chip>
                    {{/if}}
                    {{#if this.verificationSummary.changesNeeded}}
                      <Chip @intent="warning" @appearance="faded" @size="sm">
                        {{this.verificationSummary.changesNeeded}}
                        {{t "tas.course_cash.changes_needed"}}
                      </Chip>
                    {{/if}}
                    {{#if this.verificationSummary.rejected}}
                      <Chip @intent="danger" @appearance="faded" @size="sm">
                        {{this.verificationSummary.rejected}}
                        {{t "tas.course_cash.rejected"}}
                      </Chip>
                    {{/if}}
                  </div>
                {{/if}}
              </div>
              <div class="flex flex-col">
                {{#each this.allTransactionsForDisplay as |transaction|}}
                  <CardTransactionRow
                    @transaction={{transaction}}
                    @onUploadReceipt={{@onUploadReceipt}}
                    @showReceiptStatus={{this.showReceiptStatus}}
                  />
                {{/each}}
              </div>
            </div>
          {{/if}}

          {{! Balance Summary }}
          <div class="mt-6 pt-6 border-t border-gray-200" data-test-balance-section>
            <div class="flex flex-col gap-3">
              <div class="flex justify-between items-baseline">
                <span class="text-base font-medium text-gray-500">{{t
                    "tas.course_cash.amount_used"
                  }}</span>
                <span class="text-base font-semibold text-gray-700" data-test-amount-used>
                  <CurrencySuperscriptCents @amountCents={{this.amountUsed}} />
                </span>
              </div>
              <div class="flex justify-between items-baseline">
                <span class="text-base font-medium text-gray-500">{{t
                    "tas.course_cash.available_funds"
                  }}</span>
                <span class="text-base font-semibold text-gray-700" data-test-available-funds>
                  <CurrencySuperscriptCents @amountCents={{this.cardRemainingAmountCents}} />
                </span>
              </div>
            </div>
          </div>

          <div class="mt-4">
            {{#if this.isCardActive}}
              <button
                type="button"
                class="tio-anchor text-sm"
                {{on "click" this.toggleGuidelinesModal}}
              >
                {{t "tas.course_cash_guidelines.button_label"}}
              </button>
            {{/if}}
          </div>
        {{/if}}
        <div class="text-gray-700 text-xs">
          <ActionableTranslation
            @t="tas.card_holder.footer_text"
            @links={{hash
              termsOfUse=(hash
                href="https://stripe.com/legal/issuing/celtic-authorized-user-terms"
                t="tas.card_holder.footer_text_terms_link"
                target="_blank"
                rel="noopener noreferrer"
              )
            }}
          />
        </div>
      </:body>
    </Section>

    {{! Guidelines Modal }}
    <GuidelinesModal @isOpen={{this.showGuidelinesModal}} @onClose={{this.toggleGuidelinesModal}} />

    {{! Card Activity Drawer }}
    <Drawer @isOpen={{this.isActivityDrawerOpen}} @onClose={{this.closeActivityDrawer}} @size="xl">
      <div class="flex-none px-6 pt-4 pb-3 border-b border-gray-200">
        <h2 class="font-semibold text-lg text-[#231e29]">
          {{t "tuition_assistance.course_cash.activity.drawer_title"}}
        </h2>
      </div>
      {{#if this.pcsCards.length}}
        <div class="h-full overflow-y-auto">
          {{#each this.pcsCardsWithMeta key="card.id" as |item|}}
            <ActivityView
              @card={{item.card}}
              @cardNumber={{item.cardNumber}}
              @totalCards={{item.totalCards}}
            />
          {{/each}}
        </div>
      {{else}}
        <DrawerBody>
          <p class="text-sm text-[#949199] italic py-4">
            {{t "tuition_assistance.course_cash.activity.no_activity"}}
          </p>
        </DrawerBody>
      {{/if}}
    </Drawer>
  </template>
}
